<template>
  <!-- 排版一比一对照官网 .footer-purpose-wrap（仅深色底栏） -->
  <footer class="portal-footer" aria-label="站点页脚">
    <div class="footer-purpose">
      <div class="footer-info">
        <div class="logoandcontact">
          <a class="logo" href="https://www.zjsai.com/" target="_blank" rel="noopener noreferrer">
            <img :src="footLogo" width="140" height="60" alt="紫金山 www.zjsai.com" />
          </a>
          <div class="contact">
            全国免费电话：<span>{{ hotline }}</span>
          </div>
        </div>
        <div class="info">
          <p>{{ copyrightLine }}</p>
          <p>软件证书编号：{{ softwareCert }}</p>
          <p>版权所有：{{ companyName }}</p>
          <p>
            <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer">
              备案号：{{ icp }}
            </a>
          </p>
          <p>
            客服邮箱：<a :href="`mailto:${email}`">{{ email }}</a>
          </p>
          <p>公司地址：{{ address }}</p>
        </div>
        <div class="verification">
          <img :src="badge1" width="46" height="42" alt="资质认证" />
          <img :src="badge2" width="45" height="42" alt="资质认证" />
        </div>
      </div>

      <div class="contact-eq">
        <!-- 官网：content-box 为块级（不 float），二维码在电话下方，不是高度挤到侧边 -->
        <div class="content-box">
          售前电话：{{ preSalePhone }}<span class="gap">售后电话：{{ afterSalePhone }}</span>
        </div>
        <div class="content-box">售前咨询电话24H：{{ preSaleMobile }}</div>
        <div class="content-box">售后咨询电话24H：{{ afterSaleMobile }}</div>
        <div class="eq-content-box">
          <img src="https://zjsai.com/zjs.jpg" width="155" height="155" alt="扫一扫，看新品" />
          <div class="text">扫一扫，看新品</div>
        </div>
        <div class="clear"></div>
        <div class="keywords">{{ keywords }}</div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { getPortalSiteConfig } from '@/api/portal'
import footLogo from '@/assets/portal/footer/foot-logo.png'
import badge1 from '@/assets/portal/footer/footer-img1.png'
import badge2 from '@/assets/portal/footer/footer-img2.png'

defineOptions({ name: 'PortalFooter' })

/** 官网深色底栏缺省文案（CMS 可覆盖部分字段） */
const DEFAULTS = {
  hotline: '400-646-5288',
  copyright: 'Copyright @ 2012 -2026 All Rights Reserved.',
  softwareCert: '深PQ-2017-0683',
  companyName: '深圳市紫金山数字智能有限公司',
  icp: '粤ICP备2025511975号',
  email: 'kefu@zjsai.com',
  address: '广东省深圳市宝安区金海路12号汇潮科技大厦2604-2605',
  preSalePhone: '400-646-5288',
  afterSalePhone: '400-084-9990',
  preSaleMobile: '18565815288',
  afterSaleMobile: '18566775553',
  // 与官网源码一致（「经融」为官网原文）
  keywords: 'AI企业级应用  AI智能体  AI任务编排  AI数字员工  AI数字化   AI智能体   AI算法应用开发'
}

const siteConfig = ref<Record<string, string>>({})

const hotline = computed(() => siteConfig.value.hotline || DEFAULTS.hotline)
const copyrightLine = computed(() => siteConfig.value.copyright || DEFAULTS.copyright)
const softwareCert = computed(() => siteConfig.value.softwareCert || DEFAULTS.softwareCert)
const companyName = computed(() => siteConfig.value.companyName || DEFAULTS.companyName)
const icp = computed(() => siteConfig.value.icp || DEFAULTS.icp)
const email = computed(() => siteConfig.value.email || DEFAULTS.email)
const address = computed(() => siteConfig.value.address || DEFAULTS.address)
const preSalePhone = computed(() => siteConfig.value.preSalePhone || DEFAULTS.preSalePhone)
const afterSalePhone = computed(() => siteConfig.value.afterSalePhone || DEFAULTS.afterSalePhone)
const preSaleMobile = computed(() => siteConfig.value.preSaleMobile || DEFAULTS.preSaleMobile)
const afterSaleMobile = computed(() => siteConfig.value.afterSaleMobile || DEFAULTS.afterSaleMobile)
const keywords = computed(() => siteConfig.value.footerKeywords || DEFAULTS.keywords)

onMounted(async () => {
  try {
    siteConfig.value = (await getPortalSiteConfig()) || {}
  } catch {
    // CMS 不可用时用官网缺省文案
  }
})
</script>

<style lang="scss" scoped>
/* 地基：对照官网 newcss.css .footer-purpose-wrap */
.portal-footer {
  --footer-bg: #232736;
  --footer-text: #ffffff;
  --footer-muted: #c0c1c1;
  --footer-container: 1200px;

  flex-shrink: 0;
  background: var(--footer-bg);
  color: var(--footer-text);
  padding: 30px 0 calc(15px + env(safe-area-inset-bottom, 0px));
}

.footer-purpose {
  width: var(--footer-container);
  max-width: 100%;
  margin: 0 auto;
  color: var(--footer-text);
  box-sizing: border-box;

  &::after {
    content: '';
    display: table;
    clear: both;
  }
}

.footer-info {
  float: left;
}

.logoandcontact {
  overflow: hidden;
}

.logo {
  float: left;
  width: auto;

  img {
    display: block;
    width: 140px;
    height: auto;
  }
}

.contact {
  float: left;
  margin-left: 45px;
  font-size: 20px;
  line-height: 49px;
  color: var(--footer-text) !important;
  white-space: nowrap;

  span {
    font-size: 30px;
    color: var(--footer-text) !important;
  }
}

.info {
  padding: 30px 0 15px;
  font-size: 14px;
  color: var(--footer-muted);
  line-height: 26px;

  p {
    margin: 0;
  }

  a {
    color: var(--footer-muted);
    text-decoration: none;

    &:hover {
      color: var(--footer-text);
    }
  }
}

.verification {
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    display: block;
    width: auto;
    height: 42px;
  }
}

.contact-eq {
  float: right;
}

.content-box {
  margin-left: 30px;
  line-height: 40px;
  font-size: 16px;
  color: var(--footer-text) !important;
  white-space: nowrap;
}

.gap {
  margin-left: 28px;
}

.eq-content-box {
  margin-left: 30px;

  img {
    display: block;
    width: 155px;
    height: 155px;
  }

  .text {
    margin-top: 15px;
    font-size: 16px;
    color: var(--footer-text);
    text-align: left;
  }
}

.clear {
  clear: both;
  height: 0;
}

.keywords {
  text-indent: 28px;
  line-height: 40px;
  font-size: 14px;
  color: var(--footer-text);
}

@media screen and (max-width: 1200px) {
  .footer-purpose {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px 32px;
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }

  .footer-info,
  .contact-eq,
  .logo,
  .contact {
    float: none;
  }

  .footer-info {
    flex: 1 1 420px;
    min-width: 0;
  }

  .contact-eq {
    flex: 0 1 auto;
    min-width: 0;
  }

  .logoandcontact {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 24px;
  }

  .contact {
    margin-left: 0;
    white-space: normal;
    line-height: 1.4;
  }

  .content-box {
    margin-left: 0;
    white-space: normal;
  }

  .eq-content-box {
    margin-left: 0;
  }

  .gap {
    display: inline-block;
    margin-left: 16px;
  }

  .keywords {
    text-indent: 0;
    margin-top: 8px;
  }
}

@media screen and (max-width: 960px) {
  .contact-eq {
    flex: 1 1 100%;
    margin-top: 0;
  }

  .gap {
    margin-left: 0;
  }
}

@media screen and (max-width: 768px) {
  .footer-purpose {
    padding-left: 16px;
    padding-right: 16px;
  }

  .contact {
    font-size: 16px;

    span {
      font-size: 22px;
    }
  }

  .eq-content-box img {
    width: 120px;
    height: 120px;
  }
}
</style>
